import { useCallback, useEffect, useState } from 'react';
import { useContext } from 'react';
import { useAuthenticated, useNotify } from 'react-admin';
import { Loading, useDataProvider } from 'react-admin';

import { AppContext } from '@app/contexts';
import { useErrorHandling } from '@app/hooks/useErrorHandling';
import { CASLAction, useAbility } from '@app/utils/authUtils';
import { getFieldOnMainLanguage } from '@app/utils/languages';
import update from 'immutability-helper';

import { HeaderAction, HeaderActionTypes } from '@app/components/ListHeaderActions';
import { GenericList } from '@app/components/common/GenericList';
import { ColumnTypes, GenericObject } from '@app/components/common/Table/DefaultTable/types';

export const ListSeries = () => {
  const { handleChangeLoading } = useContext(AppContext);
  const [handleError] = useErrorHandling();
  const notify = useNotify();

  useAuthenticated();
  const ability = useAbility();

  const dataProvider = useDataProvider();
  const [series, setSeries] = useState<GenericObject[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState();

  const fetchSeries = useCallback(() => {
    dataProvider
      .getList('series', { pagination: { page: 0, perPage: 1000 }, sort: { field: '', order: '' }, filter: '' })
      .then(({ data }) => {
        const sortedSeries = data.sort((a: GenericObject, b: GenericObject) => {
          return (a.position as number) - (b.position as number);
        });
        setSeries(sortedSeries);
        setLoading(false);
      })
      .catch((error) => {
        setError(error);
        setLoading(false);
      });
  }, [dataProvider]);

  useEffect(() => {
    fetchSeries();
  }, [fetchSeries]);

  const deleteCallback = (id: number) => {
    setSeries(series.filter((item) => item.id !== id));
  };

  const posStatusChange = () => {
    fetchSeries();
  };

  const columns = [
    {
      label: 'NAME',
      key: 'name',
      getImagePath: (record: GenericObject) => `${(record?.logo as GenericObject)?.path}`,
      type: ColumnTypes.TITLE,
      format: (row: GenericObject) => getFieldOnMainLanguage(row?.name),
    },
    {
      label: 'DESCRIPTION',
      key: 'short_description',
      type: ColumnTypes.DEFAULT,
      format: (row: GenericObject) => getFieldOnMainLanguage(row?.short_description),
    },
    {
      label: 'FOCUSED TOPICS',
      key: 'focused_topics',
      type: ColumnTypes.PILLS,
      format: (row: GenericObject) => getFieldOnMainLanguage(row?.category_name),
    },
  ];

  if (ability.can(CASLAction.WRITE, 'series')) {
    columns.push({
      label: 'PUBLISHED',
      key: 'status',
      type: ColumnTypes.STATUS,
      // @ts-ignore definitely valid
      publishedKey: 'status',
      isPublished: (row: GenericObject) => row.status === 'ACTIVE',
      getNewPublishedStatus: (row: GenericObject) => (row.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE'),
      posStatusChange,
    });
  }

  const moveCard = useCallback((dragIndex: number, hoverIndex: number) => {
    setSeries((prevSeries) =>
      update(prevSeries, {
        $splice: [
          [dragIndex, 1],
          [hoverIndex, 0, prevSeries[dragIndex]],
        ],
      })
    );
  }, []);

  const additionalHeaderActions: HeaderAction[] | undefined = [];

  if (ability.can(CASLAction.REORDER, 'series')) {
    additionalHeaderActions.push({
      id: 2,
      type: HeaderActionTypes.REORDER,
      handler: async () => {
        try {
          setLoading(true);
          const body = series.map((item) => item.id);
          await dataProvider.reorder('series', { body });
          notify('Series have been reordered with success', { type: 'success' });
          fetchSeries();
        } catch (error: any) {
          handleError(error);
        } finally {
          setLoading(false);
        }
      },
    });
  }

  if (loading) return <Loading />;
  if (error) return <h1>{error}</h1>;
  if (!series) return null;

  const orderable = ability.can(CASLAction.REORDER, 'series');

  return (
    <GenericList
      isListLoading={loading}
      additionalHeaderActions={additionalHeaderActions}
      resourceSingular="series"
      handleChangeLoading={handleChangeLoading}
      columns={columns}
      orderable={orderable}
      deleteCallback={deleteCallback}
      reorderRecord={moveCard}
      rows={series}
      resource="series"
      title="Series"
      hideSearch
    />
  );
};
